
import {
  Component,
  Provide,
  Inject,
  Prop,
  Mixins
} from "vue-property-decorator";
import { CreateElement } from "vue";

// Mixins
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { TldsSorters } from "@/modules/web_hosting/data/sorters";
import { TldsFilters } from "@/modules/web_hosting/data/filters";

// Types
import {
  ITldsProviderData,
  ITldsProviderMethods
} from "@/modules/web_hosting/models/domains";
import { IFilter, ISorter } from "@/models/tables";
import { IProduct } from "@/models/products";
import { IProvisionCategoriesProviderData } from "@/models/providers/provisioning";
import {
  ICatalogueProductListData,
  ICatalogueProductListMethods
} from "@/models/providers/catalogueProducts";

@Component
export default class TldsProvider extends Mixins(FilteringSortingTableStore) {
  // Inject

  @Inject({ from: "provisionCategoriesProvider" })
  public readonly $provisionCategoriesData!: () => IProvisionCategoriesProviderData;

  public get categoriesData() {
    return this.$provisionCategoriesData();
  }
  // Props

  @Prop({ default: true })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  // Variables

  public dataModule = DataModules.HOST_MODULE_TLDS;

  public filters: IFilter[] = TldsFilters();
  public sorters: ISorter[] = TldsSorters();

  public isLoading = true;
  public isReloading = false;

  // Lifecycle methods

  public async created() {
    if (!this.contentIsAvailable) {
      this.isLoading = false;
      return;
    }

    this.apiProps = {
      with: "provision_provider,provision_provider.logos,category,prices,image"
    };

    this.getData();

    this.$bus.$on("refreshTlds", () => {
      this.reloadData();
    });
  }

  public beforeDestroy() {
    this.$bus.$off("refreshConfigurationsTable");
  }

  // Methods

  public async refresh() {
    this.getData({ ignoreStored: true });
  }

  public async reloadData() {
    try {
      this.isReloading = true;
      await this.cancelRequests();
      await this.getData({ ignoreStored: true });
    } finally {
      this.isReloading = false;
    }
  }

  public deleteTld(tldId: IProduct["id"]) {
    if (!this.canDelete) return;

    this.$store.dispatch(`data/${DataModules.HOST_MODULE_TLDS}/confirmDelete`, {
      tldId,
      onSuccess: this.reloadData
    });
  }

  // Computed

  public get canDelete() {
    return this.$userCan("web_hosting::delete_tld_product");
  }

  public get canUpdate() {
    return this.$userCan("web_hosting::update_tld_product");
  }

  // Guarding

  public guardFunctionalities = ["web_hosting::list_tld_products"];

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  // Life-cycle Methods

  public render(h: CreateElement) {
    if (!this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false } });

    // Handle request error
    if (this.handleRequestError && this.requestError)
      return h("request-error", {
        props: { isLoading: this.table.loading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $tldsData: this.providerData(),
        $tldsMethods: this.providerMethods,
        $catalogueProductListData: this.catalogueProductListData(),
        $catalogueProductListMethods: this.catalogueProductListMethods
      })
    );
  }

  // Provide

  @Provide("tldsProviderData")
  public providerData(): ITldsProviderData {
    return {
      canUpdate: this.canUpdate,
      canDelete: this.canDelete
    };
  }

  @Provide("catalogueProductListData")
  public catalogueProductListData(): ICatalogueProductListData {
    return {
      activeFilters: this.activeFilters,
      activeSort: this.table.sort,
      availableFilters: this.filters,
      availableSorters: this.sorters,
      config: this.config,
      defaults: this.defaults,
      enableFiltering: this.enableFiltering,
      enableSorting: this.enableSorting,
      filters: this.filters,
      hasRequestError: false,
      hasProvisioning: true,
      isLoading: this.table.loading || this.loading,
      isReloading: this.isReloading,
      limit: this.table.limit,
      page: this.table.page,
      sorters: this.sorters,
      table: this.table,
      total: this.table.total
    };
  }

  @Provide("tldsProviderMethods")
  public get providerMethods(): ITldsProviderMethods {
    return {
      deleteTld: this.deleteTld
    };
  }

  @Provide("catalogueProductListMethods")
  public get catalogueProductListMethods(): ICatalogueProductListMethods {
    return {
      getData: this.getData,
      onFiltersChange: this.onFiltersChange,
      onLimitChange: this.onLimitChange,
      onPageChange: this.onPageChange,
      onSort: this.onSort,
      reloadData: this.reloadData
    };
  }
}
